import {Injectable} from '@angular/core';
import {Environment} from '../../shared/environment/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {map, mergeMap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ActivateContentPackRequest} from '../../model/ocapi/activate-content-pack-request';
import {ActivatePackResponse} from '../../model/ocapi/activate-pack-response';
import {ActivateDaypassRequest} from '../../model/ocapi/activate-daypass-request';
import {Order} from '../../model/order';
import {SalesEntity} from '../../model/ocapi/sales-entity';
import {Trial} from '../../model/trial';
import {ActivateTrialRequest} from '../../model/ocapi/activate-trial-request';
import {OcapiUrlConstants} from '../../shared/ocapi-url-constants';
import {OmapiIdsUtils} from '../../shared/utils/omapi-ids/omapi-ids-utils';
import {ActivateStreamingBundleUtil} from '../../utils/streaming-bundles/activate-streaming-bundle.util';
import {Basket} from '../../interfaces/streaming-bundles/basket.interface';
import {SalesOrder} from '../../interfaces/sales-order/sales-order.interface';
import {NetcrackerProduct} from '../../interfaces/products/netcracker-product.interface';

@Injectable()
export class OcapiSynchronousService {

  constructor(private httpClient: HttpClient) {
  }

  public activatePack(order: Order): Observable<ActivatePackResponse> {
    let activatePackObservable;
    if (order.type === Order.DAYPASS) {
      activatePackObservable = this.activateDayPass(order);
    } else {
      activatePackObservable = this.activateContentPack(order);
    }
    return activatePackObservable;
  }

  public activateStreamingService(basket: Basket): Observable<SalesOrder> {
    const url = this.getSalesServiceActionUrl() + '/streaming-subscriptions/manage-streaming-services';
    const request = ActivateStreamingBundleUtil.createRequest(basket);
    const httpOptions = this.createHeaderOptions(OmapiIdsUtils.accessToken);

    return this.httpClient.post(url, request, httpOptions).pipe(
      mergeMap((salesOrder: SalesOrder) => {
        if (salesOrder.feasibilityCheckRequired) {
          return this.feasibilityCheck(salesOrder);
        }
        return of(salesOrder);
      }),
      mergeMap((salesOrder: SalesOrder) => {
        return this.submitSalesOrder(salesOrder);
      })
    );
  }

  public submitSalesOrder(salesOrder: SalesOrder): Observable<SalesOrder> {
    const url = this.getSalesServiceV2ActionUrl() + `/salesorders/${salesOrder.id}/submit`;
    const httpOptions = this.createHeaderOptions(OmapiIdsUtils.accessToken);
    return this.httpClient.post(url, null, httpOptions).pipe(
      map((result) => {
        return result as SalesOrder;
      })
    );
  }

  public feasibilityCheck(salesOrder: SalesOrder): Observable<SalesOrder> {
    const url = this.getSalesServiceActionUrl() + `/salesorders/${salesOrder.id}/feasibilitycheck`;
    const payload = ActivateStreamingBundleUtil.createFeasibilityRequest(salesOrder);
    const httpOptions = this.createHeaderOptions(OmapiIdsUtils.accessToken);
    return this.httpClient.post(url, payload, httpOptions).pipe(
      map((result) => {
        return result as SalesOrder;
      })
    );
  }

  public getProductsWaitingToGetActivatedForLocation(locationId: string): Observable<NetcrackerProduct[]> {
    const url = this.getProductService() + `/products?status=ACTIVE,SUSPENDED,ACTIVATION_IN_PROGRESS,DISCONNECTION_IN_PROGRESS,DISCONNECTION_PASSED_PONR,DISCONNECTED`;
    const httpOptions = this.createHeaderOptions(OmapiIdsUtils.accessToken);
    return this.httpClient.get(url, httpOptions).pipe(
      map((result: NetcrackerProduct[]) => {
        return result.filter((ncProduct) => {
          return ncProduct.locationId === locationId;
        }).map((ncProduct) => {
          ncProduct.omapiId = ncProduct.specurl.split('/').pop();
          ncProduct.options.map((option) => {
            option.omapiId = option.specurl.split('/').pop();
          });
          return ncProduct;
        });
      })
    );
  }

  public activateTrial(trial: Trial): Observable<any> {
    const activateTrialPackUrl = this.getOcapiAdaptorActionUrl() + '/?t=activatetrial';

    const body: ActivateTrialRequest = {
      dtvidentifier: OmapiIdsUtils.dtvIdentifier,
      vouchernumber: trial.voucherid,
      salesofferingspecificationid: trial.salesOfferingSpecificationId,
      salesentity: {salesentityid: '68625', salesperson: 'EOS TV Shop'} as SalesEntity
    };

    return this.httpClient.post<ActivatePackResponse>(activateTrialPackUrl, body, this.createHeaderOptions(OmapiIdsUtils.accessToken))
      .pipe(map(response => this.mappedResponse(response)));
  }

  private activateContentPack(order: Order): Observable<ActivatePackResponse> {
    const activateContentPackUrl = this.getOcapiAdaptorActionUrl() + '/?t=activatecontentpack';
    const body: ActivateContentPackRequest = {
      dtvidentifier: OmapiIdsUtils.dtvIdentifier,
      activatedproductomapiid: order.packOmapiId,
      activatedsubproductomapiid: order.subPackOmapiId ? order.subPackOmapiId : null,
      term: order.term ? Number.parseInt(order.term) : 1
    };

    return this.httpClient.post<ActivatePackResponse>(activateContentPackUrl, body, this.createHeaderOptions(OmapiIdsUtils.accessToken))
      .pipe(map(
        response => this.mappedResponse(response)
      ));
  }

  private activateDayPass(order: Order): Observable<ActivatePackResponse> {
    const activateDayPassUrl = this.getOcapiAdaptorActionUrl() + '/?t=activatedaypass';

    return this.httpClient.post<ActivatePackResponse>(activateDayPassUrl, this.createActivateDaypassBody(order.packOmapiId), this.createHeaderOptions(OmapiIdsUtils.accessToken))
      .pipe(map(response => this.mappedResponse(response)));
  }

  private createHeaderOptions(accessToken) {
    return {
      withCredentials: true,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      })
    };
  }

  private mappedResponse(parsedBody: any): ActivatePackResponse {
    return {
      id: parsedBody.id,
      success: parsedBody.success,
      errorCode: parsedBody.errorcode,
      errorMessage: parsedBody.errormessage
    } as ActivatePackResponse;
  }

  private createActivateDaypassBody(activateDayPassOmapiId: string) {
    return {
      dtvidentifier: OmapiIdsUtils.dtvIdentifier,
      omapiid: activateDayPassOmapiId,
      salesofferingspecificationid: '141',
      salesentity: {salesentityid: '68625', salesperson: 'EOS TV Shop'} as SalesEntity
    } as ActivateDaypassRequest;
  }

  private getBaseUrl(): string {
    return Environment.environmentConfig().baseApiUrl;
  }

  private getBaseUrlForInt(): string {
    return Environment.environmentConfig().baseApiUrlForInt;
  }

  private getOcapiAdaptorActionUrl(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_ADAPTOR_ACTION;
  }

  private getOcapiAdaptorActionUrlForInt(): string {
    return this.getBaseUrlForInt() + OcapiUrlConstants.OCAPI_ADAPTOR_ACTION;
  }

  private getSalesServiceActionUrl(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_SALES_SERVICE;
  }

  private getSalesServiceV2ActionUrl(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_SALES_SERVICE_V2;
  }

  private getProductService(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_PRODUCT_SERVICE;
  }
}
